import { Component } from '@angular/core';
import { Firestore, doc, writeBatch } from '@angular/fire/firestore';
import { Timestamp } from '@firebase/firestore';
import { NavController, NavParams, PopoverController } from '@ionic/angular';
import { USER_NOTIFICATION_TYPES, collections, routes } from 'src/app/constants';
import { UserNotification } from 'src/models';
import { ConversationsService } from 'src/services/conversations.service';
import { CustomersService } from 'src/services/customers.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent {
  notifications: UserNotification[] = [];
  unreadCount = 0;

  constructor(
    private afs: Firestore,
    private navController: NavController,
    private popoverCtrl: PopoverController,
    private customersService: CustomersService,
    private conversationsService: ConversationsService,
    navParams: NavParams,
    private usersService: UsersService,
  ) {
    this.notifications = navParams.get('notifications');
    this.unreadCount = navParams.get('unreadCount');

    // used to automatically mark read because notifications were not too useful
    // Now that we don't send new channel member notifications, maybe we can let them actually mark them as unread
    // this.markRead();
  }

  selectNotification(notification: UserNotification) {
    switch (notification.type) {
      case USER_NOTIFICATION_TYPES.NEW_INVITE: {
        const customerId = notification.data.organizerId;

        this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.CUSTOMERS}/${customerId}/members`);
        break;
      }
      case USER_NOTIFICATION_TYPES.OUTBOUND_SURVEY_RECEIVED: {
        if (notification.data.outboundId) {
          this.navController.navigateRoot(
            `${routes.DASHBOARD}/outbound/messages?outboundId=${notification.data.outboundId}&tab=surveyResults`,
          );
        } else {
          this.navController.navigateRoot(
            `${routes.DASHBOARD}/outbound/surveys?surveyId=${notification.data.surveyId}`,
          );
        }

        break;
      }
      case USER_NOTIFICATION_TYPES.CREATE_CUSTOMER_FAILED: {
        window.open(
          'https://docs.unthread.io/docs/setting-up-your-account/connecting-customer-channels#troubleshooting',
          '_blank',
        );
        break;
      }
      case USER_NOTIFICATION_TYPES.CREATE_CUSTOMER_SUCCEEDED: {
        this.navController.navigateRoot(`${routes.DASHBOARD}/customers`);
        break;
      }
      case USER_NOTIFICATION_TYPES.MCP_OAUTH_APPROVAL_REQUESTED: {
        const approvalKey = notification.data?.approvalKey;
        const query = typeof approvalKey === 'string' ? `?mcpOAuthApprovalKey=${encodeURIComponent(approvalKey)}` : '';

        this.navController.navigateRoot(`/${routes.DASHBOARD}/${routes.SETTINGS}/security${query}`);
        break;
      }
      case USER_NOTIFICATION_TYPES.MCP_OAUTH_CONNECTED: {
        this.navController.navigateRoot('/profile');
        break;
      }
      case USER_NOTIFICATION_TYPES.EXTERNAL_TASK_COMPLETED:
      case USER_NOTIFICATION_TYPES.RELAY_DELIVERY_FAILED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_ASSIGNED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_ESCALATED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_REMINDER:
      case USER_NOTIFICATION_TYPES.CONVERSATION_WAKE_UP:
      case USER_NOTIFICATION_TYPES.CONVERSATION_REOPENED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_FOLLOWER_ADDED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_COLLABORATOR_ADDED: {
        if (notification.data?.conversationId) {
          this.navController.navigateRoot(
            `${routes.DASHBOARD}/${routes.INBOX}/me/conversations/${notification.data.conversationId}`,
          );
        }
        break;
      }
      case USER_NOTIFICATION_TYPES.OUTBOUND_SEND_FAILED: {
        if (notification.data.outboundId) {
          this.navController.navigateRoot(
            `${routes.DASHBOARD}/outbound/messages?outboundId=${notification.data.outboundId}`,
          );
        }

        break;
      }
      case USER_NOTIFICATION_TYPES.OUTBOUND_SEND_SUCCEEDED: {
        if (notification.data.outboundId) {
          this.navController.navigateRoot(
            `${routes.DASHBOARD}/outbound/messages?outboundId=${notification.data.outboundId}`,
          );
        }

        break;
      }
      case USER_NOTIFICATION_TYPES.AI_USAGE_OVERAGE_WARNING: {
        this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.ANALYTICS}/ai?view=usage`);
        break;
      }
      case USER_NOTIFICATION_TYPES.AI_PROVIDER_CREDITS_EXHAUSTED:
      case USER_NOTIFICATION_TYPES.AI_USAGE_HARD_LIMIT_REACHED: {
        this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.SETTINGS}/ai`);
        break;
      }
      case USER_NOTIFICATION_TYPES.CUSTOMER_EXPORT_COMPLETED: {
        if (notification.data?.exportId) {
          this.downloadCustomerExport(notification.data.exportId);
        }
        break;
      }
      case USER_NOTIFICATION_TYPES.CUSTOMER_EXPORT_FAILED: {
        // Navigate to customers page - user can try again from there
        this.navController.navigateRoot(`${routes.DASHBOARD}/customers`);
        break;
      }
      case USER_NOTIFICATION_TYPES.PEOPLE_EXPORT_COMPLETED: {
        if (notification.data?.exportId) {
          this.usersService.downloadExport(notification.data.exportId).catch((error) => {
            console.error('Failed to download People export:', error);
          });
        }

        break;
      }
      case USER_NOTIFICATION_TYPES.PEOPLE_EXPORT_FAILED: {
        this.navController.navigateRoot(
          `${routes.DASHBOARD}/people/${notification.data?.onlyExternalUsers ? 'external' : 'team-members'}`,
        );
        break;
      }
      case USER_NOTIFICATION_TYPES.CONVERSATION_EXPORT_COMPLETED: {
        if (notification.data?.exportId) {
          this.downloadConversationExport(notification.data.exportId);
        }
        break;
      }
      case USER_NOTIFICATION_TYPES.CONVERSATION_EXPORT_FAILED: {
        this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}`);
        break;
      }
      case USER_NOTIFICATION_TYPES.USER_MERGE_COMPLETED:
      case USER_NOTIFICATION_TYPES.USER_MERGE_FAILED: {
        // Navigate to users page
        this.navController.navigateRoot(`${routes.DASHBOARD}/people/team-members`);
        break;
      }
      default:
        break;
    }

    this.markRead(notification);
    this.popoverCtrl.dismiss();
  }

  markRead(notification?: UserNotification) {
    const batch = writeBatch(this.afs);

    if (notification) {
      notification.read = true;
      notification.readAt = Timestamp.fromDate(new Date());

      batch.update(doc(this.afs, `${collections.NOTIFICATIONS}/${notification.objectId}`), {
        read: true,
        readAt: Timestamp.fromDate(new Date()),
      });
    } else {
      this.notifications.forEach((n) => {
        n.read = true;
        n.readAt = Timestamp.fromDate(new Date());

        batch.update(doc(this.afs, `${collections.NOTIFICATIONS}/${n.objectId}`), {
          read: true,
          readAt: Timestamp.fromDate(new Date()),
        });
      });
    }

    batch.commit();
  }

  getIcon(notification: UserNotification) {
    switch (notification.type) {
      case USER_NOTIFICATION_TYPES.NEW_INVITE:
        return 'calendar-outline';
      case USER_NOTIFICATION_TYPES.OUTBOUND_SURVEY_RECEIVED:
        return 'list-outline';
      case USER_NOTIFICATION_TYPES.CREATE_CUSTOMER_SUCCEEDED:
        return 'business-outline';
      case USER_NOTIFICATION_TYPES.CREATE_CUSTOMER_FAILED:
        return 'alert-circle-outline';
      case USER_NOTIFICATION_TYPES.EXTERNAL_TASK_COMPLETED:
        return 'checkbox-outline';
      case USER_NOTIFICATION_TYPES.RELAY_DELIVERY_FAILED:
        return 'alert-circle-outline';
      case USER_NOTIFICATION_TYPES.BULK_CONVERSATION_UPDATE_SUCCEEDED:
        return 'checkmark-outline';
      case USER_NOTIFICATION_TYPES.BULK_CONVERSATION_UPDATE_FAILED:
        return 'alert-circle-outline';
      case USER_NOTIFICATION_TYPES.OUTBOUND_SEND_SUCCEEDED:
        return 'megaphone-outline';
      case USER_NOTIFICATION_TYPES.OUTBOUND_SEND_FAILED:
        return 'alert-circle-outline';
      case USER_NOTIFICATION_TYPES.AI_USAGE_OVERAGE_WARNING:
        return 'warning-outline';
      case USER_NOTIFICATION_TYPES.AI_USAGE_HARD_LIMIT_REACHED:
      case USER_NOTIFICATION_TYPES.AI_PROVIDER_CREDITS_EXHAUSTED:
        return 'ban-outline';
      case USER_NOTIFICATION_TYPES.CUSTOMER_EXPORT_COMPLETED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_EXPORT_COMPLETED:
      case USER_NOTIFICATION_TYPES.PEOPLE_EXPORT_COMPLETED:
        return 'download-outline';
      case USER_NOTIFICATION_TYPES.CUSTOMER_EXPORT_FAILED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_EXPORT_FAILED:
      case USER_NOTIFICATION_TYPES.PEOPLE_EXPORT_FAILED:
        return 'alert-circle-outline';
      case USER_NOTIFICATION_TYPES.CONVERSATION_ASSIGNED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_ESCALATED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_REMINDER:
      case USER_NOTIFICATION_TYPES.CONVERSATION_WAKE_UP:
      case USER_NOTIFICATION_TYPES.CONVERSATION_REOPENED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_FOLLOWER_ADDED:
      case USER_NOTIFICATION_TYPES.CONVERSATION_COLLABORATOR_ADDED:
        return 'chatbubble-outline';
      case USER_NOTIFICATION_TYPES.USER_MERGE_COMPLETED:
        return 'people-outline';
      case USER_NOTIFICATION_TYPES.USER_MERGE_FAILED:
        return 'alert-circle-outline';
      case USER_NOTIFICATION_TYPES.MCP_OAUTH_APPROVAL_REQUESTED:
      case USER_NOTIFICATION_TYPES.MCP_OAUTH_CONNECTED:
        return 'shield-checkmark-outline';
      default:
        return 'calendar-outline';
    }
  }

  private async downloadCustomerExport(exportId: string) {
    try {
      await this.customersService.downloadExport(exportId);
    } catch (error) {
      console.error('Failed to download export:', error);
    }
  }

  private async downloadConversationExport(exportId: string) {
    try {
      await this.conversationsService.downloadExport(exportId);
    } catch (error) {
      console.error('Failed to download export:', error);
    }
  }
}
